import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiPlus, FiRefreshCw, FiFileText, FiClock, FiSearch, FiX, FiChevronLeft, FiChevronRight, FiChevronDown, FiZap, FiCheck, FiAlertTriangle, FiLock, FiSettings, } from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import { useRateQuoteLink, type QuoteRow, } from '../ratequotelink/RateQuoteLinkContext';
import { RateQuoteLinkImportTab } from '../ratequotelink/RateQuoteLinkImportTab';

import { ROUTES } from '../../app/routes';
import { getCommercialLineRatingStatus, enableCommercialLineRating, } from '../../services/commercialLineRatingService';
import { startCommercialRun, fetchRowRuns, fetchSkill, saveSkill, type CLRSkill, } from './useCommercialRating';
import CLRWorkflowPanel from './CLRWorkflowPanel';
import CLRRowExpand, { type CLQuoteRunEntry, type CLCarrierEntry } from './CLRRowExpand';
import CLRHistoryTab from './CLRHistoryTab';
import CLRClassicQuoteTab from './CLRClassicQuoteTab';
import logo from '../../assets/images/M_Multicolor.svg';
import rateLogo from '../../assets/images/Momentum rate Logo 1 (2).svg';
import '../../styles/RateQuoteLink.css';
type TabKey = 'classic' | 'prospects' | 'renewal' | 'import' | 'history' | 'settings';
const ROWS_PER_PAGE = 20;
const ExecuteButton: React.FC<{
    onRun: (lob: string) => void;
    busy?: boolean;
}> = ({ onRun, busy }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open)
            return;
        const onDoc = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node))
                setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);
    return (<div className="rql-exec-split" ref={ref}>
      <button type="button" className="rql-row-execute rql-exec-split__main" onClick={() => setOpen(v => !v)} disabled={busy}>
        <FiZap size={12}/>
        <span>Execute</span>
      </button>
      <button type="button" className="rql-exec-split__caret" onClick={() => setOpen(v => !v)} disabled={busy}>
        <FiChevronDown size={12}/>
      </button>
      {open && (<div className="rql-exec-menu" role="menu">
          <div className="rql-exec-menu__label-row">Select line of business</div>
          <button type="button" role="menuitem" className="rql-exec-menu__item" onClick={() => { setOpen(false); onRun('WC'); }}>
            <FiZap size={12}/>
            <span className="rql-exec-menu__text">
              <span className="rql-exec-menu__label">Workers Comp</span>
              <span className="rql-exec-menu__hint">WC — Employer liability</span>
            </span>
          </button>
          <button type="button" role="menuitem" className="rql-exec-menu__item" onClick={() => { setOpen(false); onRun('Cyber'); }}>
            <FiZap size={12}/>
            <span className="rql-exec-menu__text">
              <span className="rql-exec-menu__label">Cyber Policy</span>
              <span className="rql-exec-menu__hint">Cyber — Liability & data breach</span>
            </span>
          </button>
          <button type="button" role="menuitem" className="rql-exec-menu__item" onClick={() => { setOpen(false); onRun('GL'); }}>
            <FiZap size={12}/>
            <span className="rql-exec-menu__text">
              <span className="rql-exec-menu__label">General Liability</span>
              <span className="rql-exec-menu__hint">GL — Commercial general liability</span>
            </span>
          </button>
          <button type="button" role="menuitem" className="rql-exec-menu__item" onClick={() => { setOpen(false); onRun('BOP'); }}>
            <FiZap size={12}/>
            <span className="rql-exec-menu__text">
              <span className="rql-exec-menu__label">Business Owners Policy</span>
              <span className="rql-exec-menu__hint">BOP — Property & liability bundle</span>
            </span>
          </button>
        </div>)}
    </div>);
};
const LOB_SKILL_CFG: Record<'WC' | 'Cyber' | 'GL' | 'BOP', {
    defaultName: string;
    hintLob: string;
    namePlaceholder: string;
    textareaPlaceholder: string;
}> = {
    WC: {
        defaultName: 'WC Underwriting Profile',
        hintLob: 'WC',
        namePlaceholder: 'e.g. Landscaping WC Profile',
        textareaPlaceholder:
            'Example:\nThis client is a standard landscaping contractor — mowing, edging, trimming, leaf cleanup.\n\n'
            + '- Does NOT perform tree work, tree removal, tree trimming above ground, or logging\n'
            + '- Does NOT use restricted pesticides or herbicides (only standard lawn products)\n'
            + '- Does NOT subcontract any work\n'
            + '- Uses company vehicles for travel between job sites\n'
            + '- No snow removal, no hardscape, no irrigation installation\n'
            + '- Seasonal: No (operates year-round)',
    },
    Cyber: {
        defaultName: 'Cyber Underwriting Profile',
        hintLob: 'Cyber',
        namePlaceholder: 'e.g. Tech Startup Cyber Profile',
        textareaPlaceholder:
            'Example:\nThis client is a SaaS company offering cloud-based project management tools.\n\n'
            + '- Stores customer PII (names, emails, payment info) in AWS\n'
            + '- Annual revenue under $5M, 25 employees\n'
            + '- Has MFA enforced for all employees\n'
            + '- Does NOT process healthcare (PHI) or financial (PCI) data\n'
            + '- No prior cyber claims or breaches\n'
            + '- Uses third-party SOC 2 certified hosting\n'
            + '- Has an incident response plan in place',
    },
    GL: {
        defaultName: 'GL Underwriting Profile',
        hintLob: 'GL',
        namePlaceholder: 'e.g. Landscaping GL Profile',
        textareaPlaceholder:
            'Example:\nThis client is a landscaping company providing lawn maintenance and hardscaping services.\n\n'
            + '- Does NOT perform tree removal or trimming above 15 feet\n'
            + '- Does NOT use restricted pesticides or chemicals\n'
            + '- No subcontractors — all work done by W-2 employees\n'
            + '- No prior GL claims in the last 5 years\n'
            + '- No work performed on rooftops or elevated structures\n'
            + '- Commercial auto coverage maintained separately\n'
            + '- All employees are trained on safety protocols',
    },
    BOP: {
        defaultName: 'BOP Underwriting Profile',
        hintLob: 'BOP',
        namePlaceholder: 'e.g. Restaurant BOP Profile',
        textareaPlaceholder:
            'Example:\nThis client is a small retail store selling clothing and accessories.\n\n'
            + '- Single location, leased space (tenant, not building owner)\n'
            + '- No manufacturing or processing on premises\n'
            + '- Fire-resistive construction, no prior fire claims\n'
            + '- Burglar alarm: none, sprinklered: no\n'
            + '- No liquor sales, no firearms sales\n'
            + '- Annual revenue under $1M, 5 employees\n'
            + '- No prior BOP or property claims in the last 5 years',
    },
};
const CLRSkillSettings: React.FC = () => {
    const [activeLob, setActiveLob] = useState<'WC' | 'Cyber' | 'GL' | 'BOP'>('WC');
    const cfg = LOB_SKILL_CFG[activeLob];
    const [name, setName] = useState(cfg.defaultName);
    const [content, setContent] = useState('');
    const [savedAt, setSavedAt] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);
    useEffect(() => {
        let stale = false;
        const c = LOB_SKILL_CFG[activeLob];
        setLoading(true);
        setContent('');
        setSavedAt(null);
        setError(null);
        setName(c.defaultName);
        fetchSkill(activeLob).then(skill => {
            if (stale) return;
            if (skill) {
                setName(skill.name);
                setContent(skill.content);
                setSavedAt(skill.updatedAt);
            }
            setLoading(false);
        }).catch(() => { if (!stale) setLoading(false); });
        return () => { stale = true; };
    }, [activeLob]);
    const handleSave = useCallback(async () => {
        setSaving(true);
        setError(null);
        setSaved(false);
        try {
            const skill = await saveSkill({ lob: activeLob, name, content });
            setSavedAt(skill.updatedAt);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        }
        catch (e: any) {
            setError(e.message || 'Save failed');
        }
        finally {
            setSaving(false);
        }
    }, [activeLob, name, content]);
    if (loading) {
        return (<div className="rql-skill-settings">
        <div className="rql-table-card">
          <div className="rql-skill-settings__body">
            <div className="rql-skel" style={{ height: 48, borderRadius: 8 }}/>
            <div className="rql-skel" style={{ height: 38, width: 260, borderRadius: 8 }}/>
            <div className="rql-skel" style={{ height: 240, borderRadius: 8 }}/>
          </div>
        </div>
      </div>);
    }
    return (<div className="rql-skill-settings">
      <div className="rql-table-card">
        <div className="rql-table-card__header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="rql-table-card__title">Underwriting Skill</span>
            <select
              className="rql-skill-settings__lob-select"
              value={activeLob}
              onChange={e => setActiveLob(e.target.value as 'WC' | 'Cyber' | 'GL' | 'BOP')}
            >
              <option value="WC">Workers Comp</option>
              <option value="Cyber">Cyber Policy</option>
              <option value="GL">General Liability</option>
              <option value="BOP">Business Owners Policy</option>
            </select>
          </div>
          {savedAt && (<span className="rql-skill-settings__saved-at">
              Last saved {new Date(savedAt).toLocaleString('en-US', {
                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
            })}
            </span>)}
        </div>

        <div className="rql-skill-settings__body">
          <p className="rql-skill-settings__hint">
            Write a prompt the AI will follow when answering {cfg.hintLob} underwriting questions.
            Be specific — describe what the business does and does NOT do, and the AI will
            answer carrier questions accordingly.
          </p>

          <label className="rql-skill-settings__label">Profile name</label>
          <input className="rql-skill-settings__input" value={name} onChange={e => setName(e.target.value)} placeholder={cfg.namePlaceholder} maxLength={120}/>

          <label className="rql-skill-settings__label">Underwriting instructions</label>
          <textarea className="rql-skill-settings__textarea" value={content} onChange={e => setContent(e.target.value)} placeholder={cfg.textareaPlaceholder} rows={16}/>

          {error && <p className="rql-skill-settings__error">{error}</p>}

          <div className="rql-skill-settings__footer">
            <button className={`rql-skill-settings__save-btn${saved ? ' is-saved' : ''}`} onClick={handleSave} disabled={saving || !content.trim()}>
              {saving ? (<><FiRefreshCw size={13} className="rql-spin"/> Saving…</>) : saved ? (<><FiCheck size={13}/> Saved</>) : ('Save Skill')}
            </button>
          </div>
        </div>
      </div>
    </div>);
};
const CommercialLinesRatingPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = ROUTES.COMMERCIAL_LINES_RATING;
    const baseRe = useMemo(() => new RegExp(`^${basePath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\/?`), [basePath]);
    const urlSegments = location.pathname.replace(baseRe, '').split('/').filter(Boolean);
    const urlRunId: string | null = urlSegments[0] === 'runs' && urlSegments[1] ? urlSegments[1] : null;
    const TAB_MAP: Record<string, TabKey> = {
        '': 'classic',
        classic: 'classic',
        prospects: 'prospects',
        renewal: 'renewal',
        import: 'import',
        history: 'history',
        settings: 'settings',
    };
    const SEG_MAP: Record<TabKey, string> = {
        classic: '',
        prospects: 'prospects',
        renewal: 'renewal',
        import: 'import',
        history: 'history',
        settings: 'settings',
    };
    const tabSegment = urlRunId ? '' : (urlSegments[0] || '');
    const hasAutoQuoteParam = Boolean(new URLSearchParams(window.location.search).get('autoQuote_insured'));
    const initialTab: TabKey = urlRunId ? 'prospects' : hasAutoQuoteParam ? 'prospects' : (TAB_MAP[tabSegment] ?? 'classic');
    const insuredName: string = (location.state as any)?.insuredName ||
        localStorage.getItem('clr-last-insured') ||
        '';
    const runLob: string = (location.state as any)?.lob || 'WC';
    const [clrConfigured, setClrConfigured] = useState<boolean | null>(null);
    const [clrSetupBusy, setClrSetupBusy] = useState(false);
    const [clrSetupError, setClrSetupError] = useState<string | null>(null);
    useEffect(() => {
        getCommercialLineRatingStatus()
            .then(s => setClrConfigured(s.isEnabled))
            .catch(() => setClrConfigured(true));
    }, []);
    const handleSetupClr = useCallback(async () => {
        setClrSetupBusy(true);
        setClrSetupError(null);
        try {
            let rateJwt = localStorage.getItem('rateToken') || '';
            if (!rateJwt) {
                const amsToken = localStorage.getItem('amsToken') || '';
                const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/account/ratetoken?nctoken=${encodeURIComponent(amsToken)}`);
                if (res.ok) {
                    const data = await res.json();
                    rateJwt = data?.igToken || data?.token || '';
                    if (rateJwt)
                        localStorage.setItem('rateToken', rateJwt);
                }
            }
            if (!rateJwt) {
                setClrSetupError('Could not retrieve Rate API token. Please try again.');
                return;
            }
            const result = await enableCommercialLineRating(rateJwt);
            if (result.success) {
                setClrConfigured(true);
            }
            else if (result.code === 'NOT_SUBSCRIBED') {
                setClrSetupError('NOT_SUBSCRIBED');
            }
            else {
                setClrSetupError(result.message || 'Setup failed. Please try again.');
            }
        }
        catch {
            setClrSetupError('Setup failed. Please try again.');
        }
        finally {
            setClrSetupBusy(false);
        }
    }, []);
    const [activeTab, setActiveTabRaw] = useState<TabKey>(initialTab);
    const [search, setSearch] = useState('');
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [executingRowId, setExecutingRowId] = useState<string | null>(null);
    const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
    const [rowRunsCache, setRowRunsCache] = useState<Record<string, CLQuoteRunEntry[]>>({});
    const [rowRunsLoading, setRowRunsLoading] = useState<Record<string, boolean>>({});
    const loadRowRuns = useCallback(async (rowId: string, force = false) => {
        if (!force && (rowRunsCache[rowId] !== undefined || rowRunsLoading[rowId]))
            return;
        setRowRunsLoading(prev => ({ ...prev, [rowId]: true }));
        try {
            const data = await fetchRowRuns(rowId);
            const mapped: CLQuoteRunEntry[] = (data.runs || []).map((r: any) => {
                const quotes = r.result_payload?.quotes || [];
                // rate_url injected by backend from application_id column
                const rateUrl = r.rate_url || r.result_payload?.rate_url || null;
                return {
                    id: r.id,
                    executedAt: r.started_at
                        ? new Date(r.started_at).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                        }) + ' · ' + new Date(r.started_at).toLocaleTimeString('en-US', {
                            hour: 'numeric', minute: '2-digit',
                        })
                        : '—',
                    runStatus: r.status || 'failed',
                    durationMs: r.duration_ms,
                    errorMessage: r.error_message,
                    rateUrl: rateUrl,
                    policyType: r.policy_type ?? null,
                    lob: r.lob ?? null,
                    carriers: quotes.map((q: any): CLCarrierEntry => {
                        const isBridge = q.requiresBridging === true;
                        const hasPremium = q.premiumAmount != null && q.premiumAmount > 0;
                        const isDeclined = q.statusCode === 'DECLINED' || q.statusCode === 'DECLINE';
                        const isFailed = q.statusCode === 'FAILED';
                        let status: CLCarrierEntry['status'];
                        if (isBridge) {
                            status = 'bridged';
                        }
                        else if (isDeclined) {
                            status = 'declined';
                        }
                        else if (isFailed || (!hasPremium && !isBridge)) {
                            status = 'failed';
                        }
                        else {
                            status = 'quoted';
                        }
                        return {
                            name: q.carrierName || q.carrierId || '?',
                            premium: hasPremium ? `$${q.premiumAmount.toLocaleString()}` : '—',
                            premiumRaw: hasPremium ? q.premiumAmount : null,
                            status,
                            logoURL: q.carrierLogoURL || null,
                            bindLink: q.bindLink || q.carrierBridgingLink || null,
                            failureReason: q.failureReason || (q.failures?.[0]?.reason) || null,
                            grade: q.grade || null,
                        };
                    }),
                };
            });
            setRowRunsCache(prev => ({ ...prev, [rowId]: mapped }));
        }
        catch {
            setRowRunsCache(prev => ({ ...prev, [rowId]: [] }));
        }
        finally {
            setRowRunsLoading(prev => ({ ...prev, [rowId]: false }));
        }
    }, [rowRunsCache, rowRunsLoading]);
    const toggleExpand = useCallback((id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setExpandedRowId(prev => prev === id ? null : id);
    }, []);
    useEffect(() => {
        if (expandedRowId)
            loadRowRuns(expandedRowId);
    }, [expandedRowId]);
    const setActiveTab = useCallback((tab: TabKey) => {
        setActiveTabRaw(tab);
        setSearch('');
        setExecutingRowId(null);
        const seg = SEG_MAP[tab];
        navigate(`${basePath}${seg ? `/${seg}` : ''}`, { replace: true });
    }, [navigate, basePath]);
    const { renewals, refreshRenewals } = useRateQuoteLink();
    const [clProspects, setClProspects] = useState<QuoteRow[] | null>(null);
    const [clTotal, setClTotal] = useState(0);
    const [clPage, setClPage] = useState(1);
    const [clTotalPages, setClTotalPages] = useState(1);
    const refreshCommercialProspects = useCallback(async (opts?: {
        search?: string;
        page?: number;
        fromDate?: string;
        toDate?: string;
    }) => {
        const authToken = localStorage.getItem('authToken');
        const ncToken = localStorage.getItem('amsToken') || '';
        if (!authToken || !ncToken)
            return;
        setClProspects(null);
        const params = new URLSearchParams();
        params.set('insuredType', 'Commercial');
        if (opts?.search)
            params.set('search', opts.search);
        if (opts?.page)
            params.set('page', String(opts.page));
        if (opts?.fromDate)
            params.set('fromDate', opts.fromDate);
        if (opts?.toDate)
            params.set('toDate', opts.toDate);
        try {
            const resp = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/AMPNowcerts/prospects?${params}`, {
                headers: { Authorization: `Bearer ${authToken}`, NCToken: ncToken },
            });
            if (!resp.ok) {
                setClProspects([]);
                return;
            }
            const json = await resp.json();
            const rows: any[] = json?.prospects ?? [];
            setClProspects(rows.map((r: any): QuoteRow => ({
                id: String(r.databaseId ?? r.id ?? ''),
                date: '—',
                insured: r.commercialName || r.name || [r.firstName, r.lastName].filter(Boolean).join(' ') || '—',
                carrier: '—',
                premium: '—',
                status: 'pending',
                phone: r.phone || r.cellPhone || undefined,
                notes: [r.city, r.state || r.stateAbv].filter(Boolean).join(', ') || undefined,
                insuredId: String(r.databaseId ?? r.id ?? ''),
                rawProspect: r,
            })));
            setClTotal(json?.total ?? 0);
            setClPage(json?.page ?? 1);
            setClTotalPages(json?.totalPages ?? 1);
        }
        catch {
            setClProspects([]);
        }
    }, [(process.env.REACT_APP_Auth_Url || '')]);
    // Skip default prospect load when auto-quote will search by name instead
    const hasAutoQuote = Boolean(new URLSearchParams(window.location.search).get('autoQuote_insured'));
    useEffect(() => {
        if (!hasAutoQuote) refreshCommercialProspects({
            search: search || undefined,
            fromDate: dateRange.start || undefined,
            toDate: dateRange.end || undefined,
        });
    }, []);
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (searchTimerRef.current)
            clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            if (activeTab === 'prospects') {
                refreshCommercialProspects({
                    search: search || undefined,
                    fromDate: dateRange.start || undefined,
                    toDate: dateRange.end || undefined,
                });
            } else if (activeTab === 'renewal') {
                refreshRenewals({
                    lobType: 'commercial',
                    search: search || undefined,
                    start: dateRange.start || undefined,
                    end: dateRange.end || undefined,
                });
            }
        }, 350);
        return () => { if (searchTimerRef.current)
            clearTimeout(searchTimerRef.current); };
    }, [search, dateRange.start, dateRange.end, activeTab]);
    useEffect(() => {
        if (activeTab === 'renewal')
            refreshRenewals({
                lobType: 'commercial',
                start: dateRange.start || undefined,
                end: dateRange.end || undefined,
            });
    }, [activeTab]);
    const prospectsLoading = clProspects === null;
    const prospectsTotal = clTotal;
    const prospectsPage = clPage;
    const prospectsTotalPages = clTotalPages;
    const newRows: QuoteRow[] = useMemo(() => clProspects ?? [], [clProspects]);
    const renewalRows: QuoteRow[] = useMemo(() => renewals ?? [], [renewals]);
    const filteredRenewals = useMemo(() => {
        if (!search)
            return renewalRows;
        const q = search.toLowerCase();
        return renewalRows.filter(r => r.insured.toLowerCase().includes(q) || (r.policyNumber ?? '').toLowerCase().includes(q));
    }, [renewalRows, search]);
    const [renewalPage, setRenewalPage] = useState(0);
    const renewalTotalPages = Math.max(1, Math.ceil(filteredRenewals.length / ROWS_PER_PAGE));
    const page = activeTab === 'prospects' ? prospectsPage - 1 : renewalPage;
    const totalPages = activeTab === 'prospects' ? prospectsTotalPages : renewalTotalPages;
    const setPage = (n: number) => {
        if (activeTab === 'prospects') {
            refreshCommercialProspects({
                search: search || undefined,
                page: n + 1,
                fromDate: dateRange.start || undefined,
                toDate: dateRange.end || undefined,
            });
        }
        else {
            setRenewalPage(n);
        }
    };
    const pagedRows = activeTab === 'prospects'
        ? newRows
        : filteredRenewals.slice(renewalPage * ROWS_PER_PAGE, (renewalPage + 1) * ROWS_PER_PAGE);
    const isRenewal = activeTab === 'renewal';
    const handleExecute = useCallback((row: QuoteRow, lob: string = 'WC') => {
        setExecutingRowId(row.id);
        localStorage.setItem('clr-last-insured', row.insured);
        navigate(`${basePath}/runs/__starting__`, {
            state: { insuredName: row.insured, lob },
        });
        startCommercialRun({
            row_id: row.id,
            insured_name: row.insured,
            insured_id: row.insuredId,
            lob,
            workflow: isRenewal ? 'renewal' : 'new',
            effective_date: isRenewal && row.expirationDateRaw ? row.expirationDateRaw : undefined,
        }).then(result => {
            navigate(`${basePath}/runs/${result.runId}`, {
                replace: true,
                state: { insuredName: row.insured, lob },
            });
        }).catch(() => {
            navigate(basePath, { replace: true });
            setExecutingRowId(null);
        });
    }, [navigate, basePath, isRenewal]);
    useEffect(() => {
        if (!urlRunId)
            setExecutingRowId(null);
    }, [urlRunId]);

    // ── Auto-Quote Deep Link ──
    // When the page loads with autoQuote params (from Rate SSO deep link via URL query),
    // find the insured in the commercial prospects and auto-trigger Execute.
    // Priority: 1) exact insuredId match  2) exact name match  3) fuzzy name match
    // Also triggers a search to handle insured on page 2+ of prospects.
    // Params are passed via URL query string (not localStorage) so they can't be
    // wiped by clearLocalStorageKeepingWidgets or leaked to other tabs.
    const autoQuoteTriggeredRef = useRef(false);
    const autoQuoteSearchedRef = useRef(false);
    const autoQuoteParams = useRef(() => {
        const sp = new URLSearchParams(window.location.search);
        const insured = sp.get('autoQuote_insured');
        const lob = sp.get('autoQuote_lob');
        const insuredId = sp.get('autoQuote_insuredId');
        if (insured && lob) {
            sp.delete('autoQuote_insured');
            sp.delete('autoQuote_lob');
            sp.delete('autoQuote_insuredId');
            const clean = sp.toString();
            window.history.replaceState({}, '', `${window.location.pathname}${clean ? `?${clean}` : ''}`);
            return { insured, lob, insuredId };
        }
        return null;
    }).current;
    const autoQuoteData = useRef(autoQuoteParams());
    useEffect(() => {
        if (autoQuoteTriggeredRef.current) return;
        if (!autoQuoteData.current) return;
        const { insured } = autoQuoteData.current;

        // Search both prospects and renewals by insured name
        if (!autoQuoteSearchedRef.current) {
            autoQuoteSearchedRef.current = true;
            refreshCommercialProspects({ search: insured });
            refreshRenewals({ search: insured, lobType: 'commercial' });
            return;
        }

        // Wait for both search results
        if (!clProspects || !renewals) return;

        const { insuredId, lob } = autoQuoteData.current;
        autoQuoteTriggeredRef.current = true;

        const normalize = (s: string) => s.toLowerCase().replace(/,/g, '').replace(/\s+/g, ' ').trim();
        const target = normalize(insured);

        const findMatch = (rows: QuoteRow[]): QuoteRow | undefined => {
            if (insuredId) {
                const idMatch = rows.find(row => row.insuredId === insuredId || row.id === insuredId);
                if (idMatch) return idMatch;
            }
            const exactMatch = rows.find(row => normalize(row.insured) === target);
            if (exactMatch) return exactMatch;
            return rows.find(row => {
                const n = normalize(row.insured);
                const parts = target.split(' ');
                if (parts.length === 2 && n === `${parts[1]} ${parts[0]}`) return true;
                return n.includes(target) || target.includes(n);
            });
        };

        // Search prospects first, then renewals
        const prospectMatch = findMatch(clProspects);
        const renewalMatch = !prospectMatch ? findMatch(renewals) : undefined;
        const match = prospectMatch || renewalMatch;

        if (!match) {
            console.warn(`[AutoQuote] Insured "${insured}"${insuredId ? ` (id=${insuredId})` : ''} not found in commercial prospects or renewals`);
            refreshCommercialProspects();
            return;
        }

        // Inline execute — handleExecute uses tab-based isRenewal which won't be correct here
        const matchIsRenewal = Boolean(renewalMatch && !prospectMatch);
        const matchInsuredId = match.insuredId || insuredId || undefined;
        setExecutingRowId(match.id);
        localStorage.setItem('clr-last-insured', match.insured);
        navigate(`${basePath}/runs/__starting__`, {
            state: { insuredName: match.insured, lob },
        });
        startCommercialRun({
            row_id: match.id,
            insured_name: match.insured,
            insured_id: matchInsuredId,
            lob,
            workflow: matchIsRenewal ? 'renewal' : 'new',
            effective_date: matchIsRenewal && match.expirationDateRaw ? match.expirationDateRaw : undefined,
        }).then(result => {
            navigate(`${basePath}/runs/${result.runId}`, {
                replace: true,
                state: { insuredName: match.insured, lob },
            });
        }).catch(() => {
            navigate(basePath, { replace: true });
            setExecutingRowId(null);
        });
    }, [clProspects, renewals, navigate, basePath]);

    return (<div className="rql-page">
      <motion.div className="rql-body" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <div className="rql-main">

          
          {clrConfigured === false ? (<div className="rql-setup-state">
              <div className="rql-setup-state__logos">
                <img src={logo} alt="Momentum AMP" className="rql-setup-state__logo-amp"/>
                <span className="rql-setup-state__logo-sep">
                  {clrSetupBusy
                ? <FiRefreshCw size={13} className="rql-setup-state__orb-spin"/>
                : '\u00D7'}
                </span>
                <img src={rateLogo} alt="Momentum Rate" className="rql-setup-state__logo-rate"/>
              </div>

              <div className="rql-setup-state__title">
                Quote commercial lines in seconds —<br />AI-powered, carrier-ready.
              </div>
              <div className="rql-setup-state__desc">
                Commercial Lines Rating connects your business accounts directly to the Rate API
                through AI-driven MCP servers. Select any prospect and let our AI instantly
                generate accurate carrier quotes across all commercial lines — no manual entry,
                no back-and-forth, no switching tabs.
              </div>

              <div className="rql-setup-state__features">
                <span className="rql-setup-state__tag"><RiSparklingFill size={11}/> AI + MCP Powered</span>
                <span className="rql-setup-state__tag"><FiCheck size={12}/> Workers Comp (WC)</span>
                <span className="rql-setup-state__tag"><FiCheck size={12}/> Cyber Policy</span>
                <span className="rql-setup-state__tag"><FiCheck size={12}/> General Liability (GL)</span>
                <span className="rql-setup-state__tag"><FiCheck size={12}/> Business Owners (BOP)</span>
                <span className="rql-setup-state__tag"><FiZap size={11}/> Instant Business Quotes</span>
                <span className="rql-setup-state__tag"><FiLock size={11}/> Scoped to your account</span>
              </div>

              <div className="rql-setup-state__actions">
                <button className="rql-setup-state__btn" onClick={handleSetupClr} disabled={clrSetupBusy}>
                  {clrSetupBusy
                ? <><FiRefreshCw size={12} className="rql-setup-state__orb-spin"/> Connecting Rate API…</>
                : <><FiZap size={12}/> Click here to launch setup</>}
                </button>
              </div>

              {clrSetupError === 'NOT_SUBSCRIBED' ? (<div className="rql-setup-state__subscribe">
                  <FiLock size={14}/>
                  <div>
                    <strong>Momentum Rate subscription required</strong>
                    <p>
                      To use Commercial Lines Rating, you need an active Momentum Rate subscription.
                      Please subscribe to get started.
                    </p>
                  </div>
                </div>) : clrSetupError ? (<div className="rql-setup-state__error">
                  <FiAlertTriangle size={12}/> {clrSetupError}
                </div>) : null}

              <div className="rql-setup-state__note">
                Takes around 15–30 seconds. Your prospects list will load automatically when setup completes.
              </div>
            </div>) : clrConfigured === null ? (<div className="rql-skel">
              <div className="rql-skel__card">
                <div className="rql-skel__logos-row">
                  <div className="rql-skel__logo-circle"/>
                  <div className="rql-skel__logo-sep"/>
                  <div className="rql-skel__logo-circle"/>
                </div>
                <div className="rql-skel__line rql-skel__line--title"/>
                <div className="rql-skel__line rql-skel__line--title rql-skel__line--title-short"/>
                <div className="rql-skel__line rql-skel__line--desc"/>
                <div className="rql-skel__line rql-skel__line--desc rql-skel__line--desc-short"/>
                <div className="rql-skel__tags-row">
                  <div className="rql-skel__tag"/>
                  <div className="rql-skel__tag"/>
                  <div className="rql-skel__tag"/>
                </div>
                <div className="rql-skel__btn"/>
              </div>
            </div>) : urlRunId ? (<CLRWorkflowPanel runId={urlRunId} insuredName={insuredName} lob={runLob} onBack={() => navigate(basePath)}/>) : (<>
              
              <div className="rql-toolbar">
                <div className="rql-tabs" role="tablist">
                  <button role="tab" aria-selected={activeTab === 'classic'} className={`rql-tab${activeTab === 'classic' ? ' rql-tab--active' : ''}`} onClick={() => setActiveTab('classic')}>
                    <RiSparklingFill size={14}/> Classic Quote
                  </button>
                  <button role="tab" aria-selected={activeTab === 'prospects'} className={`rql-tab${activeTab === 'prospects' ? ' rql-tab--active' : ''}`} onClick={() => setActiveTab('prospects')}>
                    <FiPlus size={14}/> Prospects
                    <span className="rql-tab__count">{prospectsTotal || newRows.length}</span>
                  </button>
                  <button role="tab" aria-selected={activeTab === 'renewal'} className={`rql-tab${activeTab === 'renewal' ? ' rql-tab--active' : ''}`} onClick={() => setActiveTab('renewal')}>
                    <FiRefreshCw size={14}/> Renewal
                    <span className="rql-tab__count">{renewalRows.length}</span>
                  </button>
                  <button role="tab" aria-selected={activeTab === 'import'} className={`rql-tab${activeTab === 'import' ? ' rql-tab--active' : ''}`} onClick={() => setActiveTab('import')}>
                    <FiFileText size={14}/> Import
                  </button>
                  <button role="tab" aria-selected={activeTab === 'history'} className={`rql-tab${activeTab === 'history' ? ' rql-tab--active' : ''}`} onClick={() => setActiveTab('history')}>
                    <FiClock size={14}/> AI Quote History
                  </button>
                  <button role="tab" aria-selected={activeTab === 'settings'} className={`rql-tab rql-tab--settings${activeTab === 'settings' ? ' rql-tab--active' : ''}`} onClick={() => setActiveTab('settings')}>
                    <FiSettings size={14}/> Settings
                  </button>
                </div>
              </div>

              {activeTab === 'classic' ? (<CLRClassicQuoteTab onSwitchToImport={() => setActiveTab('import')} onStartQuote={({ clientId, clientName, lob }) => {
                    setExecutingRowId(clientId);
                    localStorage.setItem('clr-last-insured', clientName);
                    navigate(`${basePath}/runs/__starting__`, {
                        state: { insuredName: clientName, lob },
                    });
                    startCommercialRun({
                        row_id: clientId,
                        insured_name: clientName,
                        insured_id: clientId,
                        lob,
                        workflow: 'new',
                    }).then(result => {
                        navigate(`${basePath}/runs/${result.runId}`, {
                            replace: true,
                            state: { insuredName: clientName, lob },
                        });
                    }).catch(() => {
                        navigate(basePath, { replace: true });
                        setExecutingRowId(null);
                    });
                }} />) : activeTab === 'settings' ? (<CLRSkillSettings />) : activeTab === 'import' ? (<RateQuoteLinkImportTab lob="commercial" onQuoteStarted={(runId: string) => {
                    setActiveTab('prospects');
                    navigate(`${basePath}/runs/${runId}`, {
                        state: { insuredName: 'Imported Insured' },
                    });
                }} onInsertedProspect={() => {
                    refreshCommercialProspects({
                        search: search || undefined,
                        fromDate: dateRange.start || undefined,
                        toDate: dateRange.end || undefined,
                    });
                }}/>) : activeTab === 'history' ? (<CLRHistoryTab onOpenRun={(runId, insuredName, lob) => navigate(`${basePath}/runs/${runId}`, {
                    state: { insuredName, lob: lob || 'WC' },
                })}/>) : (<>
                  <div className="rql-filter-bar">
                    <div className="rql-hist__search">
                      <FiSearch size={13}/>
                      <input type="text" placeholder={activeTab === 'prospects' ? 'Search prospects...' : 'Search renewals...'} value={search} onChange={e => setSearch(e.target.value)}/>
                      {search && (<button className="rql-hist__date-clear" onClick={() => setSearch('')}>
                          <FiX size={11}/>
                        </button>)}
                    </div>
                    <div className="rql-hist__date-range">
                      <FiClock size={11}/>
                      <input type="date" value={dateRange.start} onChange={e => setDateRange(r => ({ ...r, start: e.target.value }))}/>
                      <span className="rql-hist__date-sep">–</span>
                      <input type="date" value={dateRange.end} onChange={e => setDateRange(r => ({ ...r, end: e.target.value }))}/>
                    </div>
                    <span className="rql-hist__count">
                      {activeTab === 'prospects' ? prospectsTotal : filteredRenewals.length}{' '}
                      {activeTab === 'prospects' ? 'prospects' : 'renewals'}
                    </span>
                  </div>

                  <div className="rql-table-card">
                    <div className="rql-table-card__header">
                      <span className="rql-table-card__title">
                        {isRenewal ? 'Renewal Quotes' : 'New Business Quotes'}
                      </span>
                      <span className="rql-table-card__subtitle">
                        {activeTab === 'prospects' ? prospectsTotal : filteredRenewals.length} records
                      </span>
                    </div>
                    <div className="rql-table-wrap">
                      <table className="rql-table">
                        <thead>
                          <tr>
                            <th className="rql-table__col-expand"/>
                            <th>Name</th>
                            {isRenewal && <th>Policy #</th>}
                            {!isRenewal && <th>Location</th>}
                            {isRenewal && <th>Line of Business</th>}
                            {isRenewal && <th>Eff. Date</th>}
                            {isRenewal && <th>Exp. Date</th>}
                            {isRenewal && <th>Premium</th>}
                            <th className="rql-table__col-action">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(activeTab === 'prospects' && prospectsLoading) ? (Array.from({ length: 6 }, (_, i) => (<tr key={`skel-${i}`} className="rql-table__row--skel">
                                <td className="rql-table__col-expand"/>
                                <td><div className="rql-skel" style={{ width: '60%' }}/></td>
                                <td><div className="rql-skel" style={{ width: '45%' }}/></td>
                                <td><div className="rql-skel" style={{ width: '30%' }}/></td>
                              </tr>))) : pagedRows.length === 0 ? (<tr>
                              <td colSpan={isRenewal ? 8 : 4}>
                                <div className="rql-empty">
                                  {search
                        ? `No ${isRenewal ? 'renewals' : 'prospects'} match "${search}".`
                        : isRenewal
                            ? 'No commercial renewals found.'
                            : 'No commercial prospects found.'}
                                </div>
                              </td>
                            </tr>) : pagedRows.map(row => {
                    const isExpanded = expandedRowId === row.id;
                    const colSpan = isRenewal ? 8 : 4;
                    return (<React.Fragment key={row.id}>
                                <tr className={`rql-table__row${isExpanded ? ' rql-table__row--expanded' : ''}`}>
                                  <td className="rql-table__col-expand" onClick={e => toggleExpand(row.id, e)}>
                                    <button type="button" className={`rql-expand-btn${isExpanded ? ' rql-expand-btn--open' : ''}`}>
                                      <FiChevronRight size={13}/>
                                    </button>
                                  </td>
                                  <td className="rql-table__cell--primary">{row.insured}</td>
                                  {isRenewal && (<td className="rql-table__cell--muted">{row.policyNumber ?? '—'}</td>)}
                                  {!isRenewal && <td>{row.notes || '—'}</td>}
                                  {isRenewal && <td>{row.lineOfBusiness || row.carrier || '—'}</td>}
                                  {isRenewal && <td>{row.effectiveDate ?? '—'}</td>}
                                  {isRenewal && <td>{row.expirationDate ?? '—'}</td>}
                                  {isRenewal && <td>{row.premium}</td>}
                                  <td className="rql-table__col-action" onClick={e => e.stopPropagation()}>
                                    <ExecuteButton onRun={(lob) => handleExecute(row, lob)} busy={executingRowId === row.id}/>
                                  </td>
                                </tr>
                                {isExpanded && (<CLRRowExpand colSpan={colSpan} runs={rowRunsCache[row.id] ?? []} isLoading={rowRunsLoading[row.id] ?? false} onOpenWorkflow={runId => {
                                const prospectName = rowRunsCache[row.id]?.[0] ? row.insured : '';
                                const runLobMatch = (rowRunsCache[row.id] ?? []).find((r: any) => r.id === runId);
                                localStorage.setItem('clr-last-insured', prospectName || row.insured);
                                navigate(`${basePath}/runs/${runId}`, {
                                    state: { insuredName: row.insured, lob: runLobMatch?.lob || 'WC' },
                                });
                            }} onRefresh={() => loadRowRuns(row.id, true)} isRenewal={isRenewal} onExecute={() => handleExecute(row)}/>)}
                              </React.Fragment>);
                })}
                        </tbody>
                      </table>
                    </div>
                    {totalPages > 1 && (<div className="rql-pagination">
                        <span className="rql-pagination__summary">
                          Showing <strong>{pagedRows.length}</strong> of{' '}
                          <strong>{activeTab === 'prospects' ? prospectsTotal : filteredRenewals.length}</strong>
                        </span>
                        <div className="rql-pagination__controls">
                          <button className="rql-pagination__btn" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>
                            <FiChevronLeft size={14}/> <span>Prev</span>
                          </button>
                          <span className="rql-pagination__page">
                            Page <strong>{page + 1}</strong> of <strong>{totalPages}</strong>
                          </span>
                          <button className="rql-pagination__btn" onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}>
                            <span>Next</span> <FiChevronRight size={14}/>
                          </button>
                        </div>
                      </div>)}
                  </div>

                  {/* RateQuotingPageChatBar removed — GlobalChatBar (shell) is the single chat entry point */}
                </>)}
            </>)}
        </div>
      </motion.div>
    </div>);
};
export default CommercialLinesRatingPage;
